import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

const DEMOS = [
  { role: "Manager", email: "admin@workforce.io", password: "Admin@123" },
  { role: "Associate", email: "associate1@workforce.io", password: "Associate@123" },
  { role: "Leadership", email: "leader1@workforce.io", password: "Leader@123" },
];

export default function Login() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from || "/dashboard";

  useEffect(() => {
    if (user) nav(from, { replace: true });
  }, [user, from, nav]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !password) return toast.error("Enter email and password");
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) {
      toast.success("Welcome back");
      nav(from, { replace: true });
    } else {
      toast.error(r.error);
    }
  };

  const quickLogin = async (d) => {
    setEmail(d.email); setPassword(d.password);
    setBusy(true);
    const r = await login(d.email, d.password);
    setBusy(false);
    if (r.ok) { toast.success(`Signed in as ${d.role}`); nav(from, { replace: true }); }
    else toast.error(r.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      {/* Left: brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[hsl(202,61%,12%)] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-[0.04] pointer-events-none" />
        <div className="relative">
          <img src="/aptia-logo.png" alt="Aptia" className="h-12 w-auto" />
        </div>
        <div className="relative">
          <div className="eyebrow text-white/50 mb-3">Enterprise Work Allocation</div>
          <h2 className="display text-5xl xl:text-6xl font-semibold tracking-tighter leading-[0.95] text-white">
            Right work,<br/>right people,<br/><span style={{color:'#00d17c'}}>right now.</span>
          </h2>
          <p className="text-white/60 mt-6 max-w-md leading-relaxed">
            An orchestration layer for high-volume operations. Automatic allocation, real-time queues, exception workflows, and executive visibility — all in one place.
          </p>
        </div>
        <div className="relative grid grid-cols-3 gap-6 mono">
          {[["01", "AUTO ALLOCATION"], ["02", "LIVE QUEUES"], ["03", "SLA CONTROL"]].map(([n, l]) => (
            <div key={n}>
              <div className="text-2xl font-semibold text-white">{n}</div>
              <div className="text-[10px] tracking-widest text-white/50 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="eyebrow text-muted-foreground mb-2">Sign in</div>
            <h1 className="display text-4xl font-semibold tracking-tighter">Welcome back.</h1>
            <p className="text-sm text-muted-foreground mt-2">Enter your credentials to continue.</p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label className="eyebrow text-muted-foreground">Email</Label>
              <Input data-testid="login-email-input" value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@company.com" className="mt-1.5 h-11" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="eyebrow text-muted-foreground">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline">Forgot?</Link>
              </div>
              <Input data-testid="login-password-input" value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="••••••••" className="h-11" />
            </div>
            <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full h-11 rounded-md text-sm">
              {busy ? "Signing in…" : "Sign In"}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t">
            <div className="eyebrow text-muted-foreground mb-3 flex items-center gap-2"><Lightning size={12} weight="fill" />Quick demo access</div>
            <div className="grid grid-cols-1 gap-2">
              {DEMOS.map(d => (
                <button
                  key={d.role}
                  data-testid={`quick-login-${d.role.toLowerCase()}`}
                  onClick={() => quickLogin(d)}
                  disabled={busy}
                  className="flex items-center justify-between border rounded-md p-3 hover:bg-secondary/60 transition-colors text-left"
                >
                  <div>
                    <div className="text-sm font-medium">Sign in as {d.role}</div>
                    <div className="text-xs text-muted-foreground mono">{d.email}</div>
                  </div>
                  <div className="text-xs text-muted-foreground">→</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
